import React, { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, Form, Modal } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { EditorElementoConfiguracionInicial } from "../Components/EditorElementoConfiguracionInicial";
import { MarcaConfiguracionInicial } from "../Components/MarcaConfiguracionInicial";
import { ProcesandoConfiguracionInicial, ConfiguracionInicialCompletada } from "../Components/EstadoFinalConfiguracionInicial";
import { NavegacionConfiguracionInicial } from "../Components/NavegacionConfiguracionInicial";
import { CierreConfiguracionInicial } from "../Components/CierreConfiguracionInicial";
import { ProgresoConfiguracionInicial } from "../Components/ProgresoConfiguracionInicial";
import { useConfiguracionInicial } from "../Hooks/useConfiguracionInicial";
import {
  CODIGO_OCURRENCIA_OMISION_PREGUNTA,
  CODIGO_OCURRENCIA_UNICA,
  crearClaveRespuesta,
  esPlanVigente,
  esEstadoProcesoDisponible,
  ESTADOS_RESPUESTA,
  obtenerPrimero,
  normalizarCarga,
} from "../Utils/configuracionInicial.constants";
import {
  obtenerDestinoBloque,
  obtenerPreguntasPendientes,
  puedeMostrarAccesoFinal,
} from "../Utils/navegacionConfiguracionInicial";
import "../configuracionInicial.css";

const PAGINA_RESUMEN = "PAGINA:RESUMEN";
const PAGINA_PLAN = "PAGINA:PLAN";
const BLOQUE_REVISION_FINAL = "B07_REVISION_FINAL";
const CODIGOS_RECORRIDO = new Set([
  "B01_NEGOCIO",
  "B02_LUGARES_PERSONAS",
  "B03_PRODUCTOS_SERVICIOS",
  "B04_VENTAS_COBROS",
  "B05_COMPRAS_PAGOS",
  "B06_IMPUESTOS_CONTABILIDAD",
  "B07_MODULOS_OPCIONALES",
]);

const ACENTOS_BLOQUE = {
  B01_NEGOCIO: "#f26b21",
  B02_LUGARES_PERSONAS: "#2f9d73",
  B03_PRODUCTOS_SERVICIOS: "#7357c8",
  B04_VENTAS_COBROS: "#2d77c7",
  B05_COMPRAS_PAGOS: "#c4872a",
  B06_IMPUESTOS_CONTABILIDAD: "#c84e63",
  B07_MODULOS_OPCIONALES: "#607d6f",
  B07_REVISION_FINAL: "#176b52",
};

const ACENTOS_TEXTO_BLOQUE = {
  B01_NEGOCIO: "#96370c",
  B02_LUGARES_PERSONAS: "#176b52",
  B03_PRODUCTOS_SERVICIOS: "#59409d",
  B04_VENTAS_COBROS: "#245eaa",
  B05_COMPRAS_PAGOS: "#785300",
  B06_IMPUESTOS_CONTABILIDAD: "#96354a",
  B07_MODULOS_OPCIONALES: "#3f5f50",
  B07_REVISION_FINAL: "#176b52",
};

const estadoDe = (respuesta) => obtenerPrimero(
  respuesta,
  ["EstadoRespuesta", "estadoRespuesta", "Estado", "estado"],
  ESTADOS_RESPUESTA.SIN_RESPONDER
);

const esResuelta = (estado) =>
  estado === ESTADOS_RESPUESTA.RESPONDIDA || estado === ESTADOS_RESPUESTA.OMITIDA;

const contextosDe = (definicion) => (definicion?.Bloques || []).flatMap((bloque) =>
  bloque.Secciones.flatMap((seccion) =>
    seccion.Elementos.map((elemento) => ({ bloque, seccion, elemento }))
  )
).sort((izquierda, derecha) =>
  (izquierda.bloque.Orden - derecha.bloque.Orden) ||
  (izquierda.seccion.Orden - derecha.seccion.Orden) ||
  (izquierda.elemento.Orden - derecha.elemento.Orden)
);

const compararPosicion = (izquierda, derecha) =>
  (izquierda.bloque.Orden - derecha.bloque.Orden) ||
  (izquierda.seccion.Orden - derecha.seccion.Orden) ||
  (izquierda.elemento.Orden - derecha.elemento.Orden);

const mensajeSeguroDeError = (error) => obtenerPrimero(
  error,
  ["Mensaje", "mensaje", "Message", "message"],
  "No fue posible aplicar la configuración. Revisa el plan e inténtalo nuevamente."
);

export const ConfiguracionInicialView = () => {
  const navigate = useNavigate();
  const {
    estadoAcceso,
    proceso,
    definicion,
    respuestas,
    indiceRespuestas,
    plan,
    valoresActuales,
    sugerencias,
    diferencias,
    indiceValoresActuales,
    indiceSugerencias,
    contextoIntegracionDisponible,
    aplicacionOperativaHabilitada,
    seccionesOmitidas,
    progreso,
    bienvenidaVisible,
    consultandoEstado,
    cargando,
    guardando,
    error,
    disponible,
    puedeAcceder,
    iniciarYCargar,
    guardarRespuesta,
    guardarRespuestas,
    guardarUbicacion,
    posponer,
    finalizarAsistente,
    faseCierre,
    limpiarError,
  } = useConfiguracionInicial();
  const inicioSolicitado = useRef(false);
  const ubicacionRestaurada = useRef(false);
  const [pagina, setPagina] = useState(null);
  const [codigoElementoActivo, setCodigoElementoActivo] = useState(null);
  const [codigoOcurrenciaActivo, setCodigoOcurrenciaActivo] = useState(CODIGO_OCURRENCIA_UNICA);
  const [recorridoMovilVisible, setRecorridoMovilVisible] = useState(false);
  const [intentoCierre, setIntentoCierre] = useState(false);
  const [cierreCompleto, setCierreCompleto] = useState(false);
  const finalizandoRef = useRef(false);
  const cierreAtencionRef = useRef(null);
  const [omitirSeccion, setOmitirSeccion] = useState(false);
  const [motivoSeccion, setMotivoSeccion] = useState("");
  const recorridoTriggerRef = useRef(null);
  const recorridoRef = useRef(null);

  const bloques = definicion?.Bloques || [];
  const bloquesRecorrido = useMemo(
    () => bloques.filter((bloque) => CODIGOS_RECORRIDO.has(bloque.Codigo)),
    [bloques]
  );
  const secciones = useMemo(() => bloques.flatMap((bloque) =>
    bloque.Secciones.map((seccion) => ({ bloque, seccion }))
  ), [bloques]);
  const elementosNavegables = useMemo(() => secciones.flatMap(({ bloque, seccion }) =>
    seccion.Elementos.map((elemento) => ({ bloque, seccion, elemento }))
  ), [secciones]);
  const preguntasPendientes = useMemo(
    () => obtenerPreguntasPendientes(definicion, respuestas, seccionesOmitidas),
    [definicion, respuestas, seccionesOmitidas]
  );
  const accesoFinalDisponible = useMemo(
    () => puedeMostrarAccesoFinal(definicion, respuestas, seccionesOmitidas),
    [definicion, respuestas, seccionesOmitidas]
  );
  const planVigente = esPlanVigente(plan, proceso);
  const modoAplicacion = contextoIntegracionDisponible === true && aplicacionOperativaHabilitada === true;

  useEffect(() => {
    if (intentoCierre && !guardando && (preguntasPendientes.length || error)) {
      cierreAtencionRef.current?.focus();
      cierreAtencionRef.current?.scrollIntoView?.({ block: 'start' });
    }
  }, [intentoCierre, guardando, preguntasPendientes.length, error]);

  useEffect(() => {
    if (bienvenidaVisible || proceso || !estadoAcceso || !disponible || !puedeAcceder || inicioSolicitado.current) return;
    const estado = obtenerPrimero(estadoAcceso, ["Estado", "estado"], null);
    if (!esEstadoProcesoDisponible(estado)) return;
    inicioSolicitado.current = true;
    iniciarYCargar().catch(() => { inicioSolicitado.current = false; });
  }, [bienvenidaVisible, disponible, estadoAcceso, iniciarYCargar, proceso, puedeAcceder]);

  useEffect(() => {
    if (!definicion || ubicacionRestaurada.current || secciones.length === 0) return;
    const elementoGuardado = obtenerPrimero(proceso, ["CodigoElementoActual", "codigoElementoActual"], null);
    const ocurrenciaGuardada = obtenerPrimero(
      proceso,
      ["CodigoOcurrenciaActual", "codigoOcurrenciaActual"],
      CODIGO_OCURRENCIA_UNICA
    ) || CODIGO_OCURRENCIA_UNICA;
    const etapaGuardada = obtenerPrimero(proceso, ["CodigoEtapaActual", "codigoEtapaActual"], null);
    if (elementoGuardado === PAGINA_RESUMEN || elementoGuardado === PAGINA_PLAN) {
      setPagina(elementoGuardado);
      setCodigoElementoActivo(elementoGuardado);
      setCodigoOcurrenciaActivo(CODIGO_OCURRENCIA_UNICA);
      ubicacionRestaurada.current = true;
      return;
    }
    const encontrada = secciones.find(({ seccion }) =>
      seccion.Elementos.some((elemento) => elemento.Codigo === elementoGuardado)
    ) || secciones.find(({ seccion }) => seccion.Codigo === etapaGuardada)
      || secciones.find(({ bloque }) => bloque.Codigo === etapaGuardada)
      || secciones[0];
    const elementoRestaurado = encontrada.seccion.Elementos.find(
      (elemento) => elemento.Codigo === elementoGuardado
    );
    setPagina(encontrada.seccion.Codigo);
    setCodigoElementoActivo(elementoRestaurado?.Codigo || encontrada.seccion.Elementos[0]?.Codigo || null);
    setCodigoOcurrenciaActivo(ocurrenciaGuardada);
    ubicacionRestaurada.current = true;
  }, [definicion, proceso, secciones]);

  useEffect(() => {
    if (!ubicacionRestaurada.current || !pagina || pagina === PAGINA_RESUMEN || pagina === PAGINA_PLAN) return;
    const seleccion = secciones.find(({ seccion }) => seccion.Codigo === pagina);
    if (!seleccion) {
      if (secciones.length > 0) {
        setPagina(secciones[0].seccion.Codigo);
        setCodigoElementoActivo(secciones[0].seccion.Elementos[0]?.Codigo || null);
        setCodigoOcurrenciaActivo(CODIGO_OCURRENCIA_UNICA);
      }
      return;
    }
    if (!seleccion.seccion.Elementos.some((elemento) => elemento.Codigo === codigoElementoActivo)) {
      setCodigoElementoActivo(seleccion.seccion.Elementos[0]?.Codigo || null);
      setCodigoOcurrenciaActivo(CODIGO_OCURRENCIA_UNICA);
    }
  }, [codigoElementoActivo, pagina, secciones]);

  useEffect(() => {
    if (!recorridoMovilVisible || !recorridoRef.current) return undefined;
    const panel = recorridoRef.current;
    const obtenerFoco = () => Array.from(panel.querySelectorAll(
      'button:not([disabled]), a[href], input:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )).filter((elemento) => elemento.offsetParent !== null);
    const elementos = obtenerFoco();
    elementos[0]?.focus();
    const manejarTecla = (evento) => {
      if (evento.key === "Escape") {
        evento.preventDefault();
        setRecorridoMovilVisible(false);
        return;
      }
      if (evento.key !== "Tab") return;
      const enfocables = obtenerFoco();
      if (enfocables.length === 0) return;
      const primero = enfocables[0];
      const ultimo = enfocables[enfocables.length - 1];
      if (evento.shiftKey && document.activeElement === primero) {
        evento.preventDefault();
        ultimo.focus();
      } else if (!evento.shiftKey && document.activeElement === ultimo) {
        evento.preventDefault();
        primero.focus();
      }
    };
    document.addEventListener("keydown", manejarTecla);
    return () => {
      document.removeEventListener("keydown", manejarTecla);
      recorridoTriggerRef.current?.focus();
    };
  }, [recorridoMovilVisible]);

  useEffect(() => {
    const contenedor = document.querySelector(".ci-wizard__scroll");
    if (contenedor) contenedor.scrollTop = 0;
  }, [codigoElementoActivo, pagina]);

  const seleccionActual = secciones.find(({ seccion }) => seccion.Codigo === pagina);
  const elementoActual = seleccionActual?.seccion.Elementos.find(
    (elemento) => elemento.Codigo === codigoElementoActivo
  ) || seleccionActual?.seccion.Elementos[0] || null;
  const contextoElementoActual = elementoActual
    ? elementosNavegables.find(({ elemento }) => elemento.Codigo === elementoActual.Codigo)
    : null;
  const indiceElementoActual = contextoElementoActual
    ? elementosNavegables.indexOf(contextoElementoActual)
    : -1;
  const indiceSeccionActual = seleccionActual ? secciones.indexOf(seleccionActual) : -1;
  const bloqueActual = contextoElementoActual?.bloque || seleccionActual?.bloque || null;
  const codigoBloqueActual = pagina === PAGINA_RESUMEN || pagina === PAGINA_PLAN
    ? BLOQUE_REVISION_FINAL
    : bloqueActual?.Codigo || null;
  const indiceEtapa = bloquesRecorrido.findIndex((bloque) => bloque.Codigo === codigoBloqueActual);
  const etiquetaEtapa = indiceEtapa >= 0
    ? `Etapa ${indiceEtapa + 1} de ${bloquesRecorrido.length}`
    : "Revisión final";
  const elementosBloqueActual = bloqueActual
    ? elementosNavegables.filter(({ bloque }) => bloque.Codigo === bloqueActual.Codigo)
    : [];
  const indicePreguntaBloque = elementoActual
    ? elementosBloqueActual.findIndex(({ elemento }) => elemento.Codigo === elementoActual.Codigo)
    : -1;
  const porcentaje = Math.min(100, Math.max(0, Number(
    obtenerPrimero(progreso, ["Porcentaje", "porcentaje"], 0)
  ) || 0));

  const estadosBloque = useMemo(() => {
    const filas = Array.isArray(respuestas) ? respuestas : [];
    return Object.fromEntries(bloquesRecorrido.map((bloque) => {
      let resueltas = 0;
      let tocadas = 0;
      let total = 0;
      bloque.Secciones.forEach((seccion) => {
        const seccionOmitida = seccionesOmitidas.includes(seccion.Codigo);
        seccion.Elementos.forEach((elemento) => {
          total += 1;
          if (seccionOmitida) {
            resueltas += 1;
            tocadas += 1;
            return;
          }
          if (elemento.EsRepetible || elemento.TipoRespuesta === "REPEATABLE_FORM") {
            const asociadas = filas.filter((respuesta) =>
              obtenerPrimero(respuesta, ["CodigoElemento", "codigoElemento"]) === elemento.Codigo
            );
            const marcador = asociadas.find((respuesta) =>
              obtenerPrimero(respuesta, ["CodigoOcurrencia", "codigoOcurrencia"]) === CODIGO_OCURRENCIA_OMISION_PREGUNTA
            );
            const operativas = asociadas.filter((respuesta) =>
              obtenerPrimero(respuesta, ["CodigoOcurrencia", "codigoOcurrencia"]) !== CODIGO_OCURRENCIA_OMISION_PREGUNTA
            );
            if (asociadas.length > 0) tocadas += 1;
            if (estadoDe(marcador) === ESTADOS_RESPUESTA.OMITIDA ||
              (operativas.length > 0 && operativas.every((respuesta) => esResuelta(estadoDe(respuesta))))) {
              resueltas += 1;
            }
            return;
          }
          const respuesta = indiceRespuestas.get(crearClaveRespuesta(elemento.Codigo, CODIGO_OCURRENCIA_UNICA));
          const estado = estadoDe(respuesta);
          if (respuesta) tocadas += 1;
          if (esResuelta(estado)) resueltas += 1;
        });
      });
      let etiqueta = "Sin empezar";
      if (total > 0 && resueltas === total) etiqueta = "Completada";
      else if (tocadas > 0) etiqueta = "Pendiente";
      if (bloque.Codigo === codigoBloqueActual) etiqueta = "En progreso";
      return [bloque.Codigo, etiqueta];
    }));
  }, [bloquesRecorrido, codigoBloqueActual, indiceRespuestas, respuestas, seccionesOmitidas]);

  const confirmarAbandonoEdicion = () => {
    const editorSucio = document.querySelector('[data-ci-dirty="true"]');
    if (!editorSucio) return true;
    return window.confirm(
      "Hay cambios sin guardar en esta pregunta. Pulsa Cancelar para guardarlos o Aceptar para descartarlos y continuar."
    );
  };

  const navegarAElemento = async ({ bloque, seccion, elemento }, ocurrencia = CODIGO_OCURRENCIA_UNICA) => {
    if (elemento.Codigo === codigoElementoActivo && ocurrencia === codigoOcurrenciaActivo) {
      setRecorridoMovilVisible(false);
      return true;
    }
    if (!confirmarAbandonoEdicion()) return false;
    try {
      await guardarUbicacion({
        codigoEtapaActual: bloque.Codigo,
        codigoElementoActual: elemento.Codigo,
        codigoOcurrenciaActual: ocurrencia,
      });
      setPagina(seccion.Codigo);
      setCodigoElementoActivo(elemento.Codigo);
      setCodigoOcurrenciaActivo(ocurrencia);
      setRecorridoMovilVisible(false);
      return true;
    } catch (_error) {
      return false;
    }
  };

  const navegarABloque = async (bloque) => {
    const destino = obtenerDestinoBloque(bloque, definicion, respuestas, seccionesOmitidas);
    if (!destino) return;
    await navegarAElemento(destino, destino.codigoOcurrencia);
  };

  const navegarAPagina = async (paginaDestino) => {
    if (paginaDestino === pagina) {
      setRecorridoMovilVisible(false);
      return true;
    }
    if (!confirmarAbandonoEdicion()) return false;
    try {
      await guardarUbicacion({
        codigoEtapaActual: BLOQUE_REVISION_FINAL,
        codigoElementoActual: paginaDestino,
        codigoOcurrenciaActual: CODIGO_OCURRENCIA_UNICA,
      });
      setPagina(paginaDestino);
      setCodigoElementoActivo(paginaDestino);
      setCodigoOcurrenciaActivo(CODIGO_OCURRENCIA_UNICA);
      setRecorridoMovilVisible(false);
      return true;
    } catch (_error) {
      return false;
    }
  };

  const navegarACodigoElemento = async (
    codigoElemento,
    codigoOcurrencia = CODIGO_OCURRENCIA_UNICA
  ) => {
    const destino = elementosNavegables.find(({ elemento }) => elemento.Codigo === codigoElemento);
    if (!destino) return false;
    return navegarAElemento(destino, codigoOcurrencia || CODIGO_OCURRENCIA_UNICA);
  };

  const aplicarDestinoLocal = (destino) => {
    if (destino.pagina === PAGINA_RESUMEN || destino.pagina === PAGINA_PLAN) {
      setPagina(destino.pagina);
      setCodigoElementoActivo(destino.pagina);
      setCodigoOcurrenciaActivo(CODIGO_OCURRENCIA_UNICA);
    } else {
      setPagina(destino.seccion.Codigo);
      setCodigoElementoActivo(destino.elemento.Codigo);
      setCodigoOcurrenciaActivo(CODIGO_OCURRENCIA_UNICA);
    }
    setRecorridoMovilVisible(false);
  };

  const destinoPosterior = (definicionActualizada, origen = contextoElementoActual) => {
    const siguiente = contextosDe(definicionActualizada).find((contexto) =>
      origen && compararPosicion(contexto, origen) > 0
    );
    if (siguiente) {
      return {
        ...siguiente,
        pagina: siguiente.seccion.Codigo,
        ubicacion: {
          codigoEtapaActual: siguiente.bloque.Codigo,
          codigoElementoActual: siguiente.elemento.Codigo,
          codigoOcurrenciaActual: CODIGO_OCURRENCIA_UNICA,
        },
      };
    }
    return {
      pagina: PAGINA_RESUMEN,
      ubicacion: {
        codigoEtapaActual: BLOQUE_REVISION_FINAL,
        codigoElementoActual: PAGINA_RESUMEN,
        codigoOcurrenciaActual: CODIGO_OCURRENCIA_UNICA,
      },
    };
  };

  const guardarRespuestaYAvanzar = async (solicitud) => {
    const origen = contextoElementoActual;
    const carga = await guardarRespuesta(solicitud);
    const destino = destinoPosterior(carga.definicion, origen);
    const versionActualizada = obtenerPrimero(
      carga.proceso,
      ["VersionRegistroProceso", "versionRegistroProceso", "VersionRegistro", "versionRegistro"],
      null
    );
    await guardarUbicacion(destino.ubicacion, versionActualizada);
    aplicarDestinoLocal(destino);
  };

  const guardarRespuestasYAvanzar = async (solicitudes) => {
    const origen = contextoElementoActual;
    const carga = await guardarRespuestas(solicitudes);
    const destino = destinoPosterior(carga.definicion, origen);
    const versionActualizada = obtenerPrimero(
      carga.proceso,
      ["VersionRegistroProceso", "versionRegistroProceso", "VersionRegistro", "versionRegistro"],
      null
    );
    await guardarUbicacion(destino.ubicacion, versionActualizada);
    aplicarDestinoLocal(destino);
  };

  const activarElemento = (codigoElemento, codigoOcurrencia = CODIGO_OCURRENCIA_UNICA) => {
    if (!seleccionActual || !codigoElemento ||
      (codigoElementoActivo === codigoElemento && codigoOcurrenciaActivo === codigoOcurrencia)) return;
    // Cambiar el foco dentro de una pregunta repetible es una acción local. La
    // ubicación se persiste al guardar la fila o al navegar; hacer una petición
    // aquí bloqueaba y deshabilitaba el control que el usuario acababa de pulsar.
    setCodigoElementoActivo(codigoElemento);
    setCodigoOcurrenciaActivo(codigoOcurrencia);
  };

  const irAnterior = async () => {
    if (pagina === PAGINA_RESUMEN || pagina === PAGINA_PLAN) {
      if (elementosNavegables.length > 0) await navegarAElemento(elementosNavegables[elementosNavegables.length - 1]);
      return;
    }
    if (seleccionActual && seccionesOmitidas.includes(seleccionActual.seccion.Codigo)) {
      const anteriorSeccion = [...elementosNavegables].reverse().find((contexto) =>
        compararPosicion(contexto, contextoElementoActual) < 0 &&
        contexto.seccion.Codigo !== seleccionActual.seccion.Codigo
      );
      if (anteriorSeccion) await navegarAElemento(anteriorSeccion);
      return;
    }
    if (indiceElementoActual > 0) await navegarAElemento(elementosNavegables[indiceElementoActual - 1]);
  };

  const irSiguiente = async () => {
    if (pagina === PAGINA_PLAN || pagina === PAGINA_RESUMEN) {
      await finalizar();
      return;
    }
    if (seleccionActual && seccionesOmitidas.includes(seleccionActual.seccion.Codigo)) {
      const siguienteSeccion = elementosNavegables.find((contexto) =>
        compararPosicion(contexto, contextoElementoActual) > 0 &&
        contexto.seccion.Codigo !== seleccionActual.seccion.Codigo
      );
      if (siguienteSeccion) await navegarAElemento(siguienteSeccion);
      else await navegarAPagina(PAGINA_RESUMEN);
      return;
    }
    if (indiceElementoActual === elementosNavegables.length - 1) {
      await navegarAPagina(PAGINA_RESUMEN);
      return;
    }
    if (indiceElementoActual >= 0) await navegarAElemento(elementosNavegables[indiceElementoActual + 1]);
  };

  const confirmarOmisionSeccion = async () => {
    if (!seleccionActual || motivoSeccion.trim().length < 5) return;
    const siguiente = secciones[indiceSeccionActual + 1] || secciones[indiceSeccionActual - 1];
    const destino = siguiente || seleccionActual;
    const primerElementoDestino = destino.seccion.Elementos[0]?.Codigo || null;
    try {
      await guardarRespuesta({
        codigoElemento: `SECCION:${seleccionActual.seccion.Codigo}`,
        estadoRespuesta: ESTADOS_RESPUESTA.OMITIDA,
        contenidoRespuestaJson: null,
        observacion: motivoSeccion.trim(),
        codigoEtapaActual: destino.bloque.Codigo,
        codigoElementoActual: primerElementoDestino,
        codigoOcurrenciaActual: CODIGO_OCURRENCIA_UNICA,
      });
      setOmitirSeccion(false);
      setMotivoSeccion("");
      if (siguiente) {
        setPagina(siguiente.seccion.Codigo);
        setCodigoElementoActivo(primerElementoDestino);
        setCodigoOcurrenciaActivo(CODIGO_OCURRENCIA_UNICA);
      } else {
        await navegarAPagina(PAGINA_RESUMEN);
      }
    } catch (_error) {
      // El provider expone el error junto al recorrido.
    }
  };

  const reabrirSeccion = async () => {
    if (!seleccionActual) return;
    try {
      await guardarRespuesta({
        codigoElemento: `SECCION:${seleccionActual.seccion.Codigo}`,
        estadoRespuesta: ESTADOS_RESPUESTA.SIN_RESPONDER,
        contenidoRespuestaJson: null,
        codigoEtapaActual: seleccionActual.bloque.Codigo,
        codigoElementoActual: seleccionActual.seccion.Elementos[0]?.Codigo || null,
      });
    } catch (_error) {
      // El provider expone el error junto al recorrido.
    }
  };

  const finalizar = async () => {
    if (guardando || finalizandoRef.current) return;
    setIntentoCierre(true);
    limpiarError();
    if (preguntasPendientes.length > 0) return;
    finalizandoRef.current = true;
    try {
      const resultado = await finalizarAsistente();
      if (obtenerPrimero(normalizarCarga(resultado).proceso, ['Estado', 'estado']) === 'COMPLETADA') {
        setCierreCompleto(contextoIntegracionDisponible ? 'APLICADA' : 'GUARDADA');
      }
    } catch (_error) {
      // El provider expone el error junto al recorrido.
    } finally {
      finalizandoRef.current = false;
    }
  };

  const salirTemporalmente = async () => {
    if (guardando || !confirmarAbandonoEdicion()) return;
    try {
      await posponer();
      navigate("/");
    } catch (_error) {
      // Stay on the wizard so a failed exit does not hide the error or lose the draft.
    }
  };

  // La aplicación mantiene el usuario y sus menús en el provider de React.
  // Al terminar el wizard necesitamos reconstruir ese estado desde la sesión
  // ya actualizada, no volver al ERP mediante una navegación SPA.
  const regresarAlErp = () => window.location.replace("/");

  const estado = obtenerPrimero(estadoAcceso, ["Estado", "estado"], null);
  if (cierreCompleto || estado === 'COMPLETADA') {
    return <ConfiguracionInicialCompletada soloPlan={cierreCompleto === 'GUARDADA'} onContinuar={regresarAlErp} />;
  }

  if (faseCierre) return <ProcesandoConfiguracionInicial fase={faseCierre} />;

  if ((!estadoAcceso && (consultandoEstado || !error)) || cargando) {
    return <ProcesandoConfiguracionInicial />;
  }

  if (!estadoAcceso && error) {
    return (
      <div className="ci-system-state" role="main">
        <Alert variant="danger">No pudimos consultar la configuración inicial. El resto del ERP continúa disponible.</Alert>
        <Button className="ci-button ci-button--secondary" variant="outline-secondary" onClick={() => navigate("/")}>Volver al ERP</Button>
      </div>
    );
  }

  if (!disponible || !puedeAcceder || !esEstadoProcesoDisponible(estado)) {
    return (
      <div className="ci-system-state" role="main">
        <Alert variant="info">
          {estado === "OMITIDA" ? "La configuración inicial fue omitida." :
              "La configuración inicial no está disponible para esta sesión."}
        </Alert>
        <Button className="ci-button ci-button--secondary" variant="outline-secondary" onClick={() => navigate("/")}>Volver al ERP</Button>
      </div>
    );
  }

  const seccionEstaOmitida = seleccionActual && seccionesOmitidas.includes(seleccionActual.seccion.Codigo);
  const anteriorDeshabilitado = guardando ||
    (indiceElementoActual <= 0 && pagina !== PAGINA_RESUMEN && pagina !== PAGINA_PLAN);
  const siguienteDeshabilitado = guardando;
  const etiquetaSiguiente = pagina === PAGINA_PLAN || pagina === PAGINA_RESUMEN
    ? faseCierre || (contextoIntegracionDisponible ? "Finalizar y aplicar" : "Finalizar asistente")
      : indiceElementoActual === elementosNavegables.length - 1
        ? "Continuar al cierre"
        : "Continuar";
  const acentoActual = ACENTOS_BLOQUE[codigoBloqueActual] || ACENTOS_BLOQUE.B01_NEGOCIO;
  const acentoTextoActual = ACENTOS_TEXTO_BLOQUE[codigoBloqueActual] || ACENTOS_TEXTO_BLOQUE.B01_NEGOCIO;

  return (
    <div className="ci-wizard" role="main" style={{ "--ciw-stage": acentoActual, "--ciw-stage-text": acentoTextoActual }}>
      <button
        type="button"
        className={`ci-wizard__backdrop${recorridoMovilVisible ? " is-visible" : ""}`}
        aria-label="Cerrar recorrido"
        onClick={() => setRecorridoMovilVisible(false)}
      />
      <div
        className={`ci-wizard__sidebar${recorridoMovilVisible ? " is-open" : ""}`}
        id="ci-recorrido"
        ref={recorridoRef}
        role={recorridoMovilVisible ? "dialog" : undefined}
        aria-modal={recorridoMovilVisible ? "true" : undefined}
        aria-label="Recorrido de configuración"
      >
        <div className="ci-brand">
          <div className="ci-brand__copy">
            <MarcaConfiguracionInicial />
          </div>
          <button type="button" className="ci-sidebar-close" aria-label="Cerrar recorrido" onClick={() => setRecorridoMovilVisible(false)}>×</button>
        </div>
        <ProgresoConfiguracionInicial progreso={progreso} etiquetaEtapa={etiquetaEtapa} />
        <NavegacionConfiguracionInicial
          bloques={bloquesRecorrido}
          codigoBloqueActual={codigoBloqueActual}
          estadosBloque={estadosBloque}
          onSeleccionar={navegarABloque}
          onSeleccionarResumen={() => navegarAPagina(PAGINA_RESUMEN)}
          paginaActual={pagina}
          mostrarAccesoFinal={accesoFinalDisponible}
          disabled={guardando}
        />
        <div className="ci-help-card">
          <span className="ci-help-card__icon" aria-hidden="true">?</span>
          <div>
            <strong>¿Necesitas ayuda?</strong>
            <p>Contacta al equipo de soporte.</p>
          </div>
        </div>
      </div>

      <div className="ci-wizard__main" role="region" aria-label="Contenido de la configuración inicial">
        <div className="ci-wizard-topbar">
          <div className="ci-mobile-brand">
            <div>
              <MarcaConfiguracionInicial />
              <span>{etiquetaEtapa}</span>
            </div>
          </div>
          <button
            type="button"
            className="ci-mobile-journey"
            ref={recorridoTriggerRef}
            aria-controls="ci-recorrido"
            aria-expanded={recorridoMovilVisible}
            onClick={() => setRecorridoMovilVisible(true)}
          >
            Ver recorrido
          </button>
          <span className="ci-context-chip">Configuración inicial</span>
          <div className="ci-wizard-topbar__actions">
            {seleccionActual && (
              seccionEstaOmitida ? (
                <button type="button" className="ci-topbar-action" disabled={guardando} onClick={reabrirSeccion}>Reabrir sección</button>
              ) : (
                <button type="button" className="ci-topbar-action" disabled={guardando} onClick={() => setOmitirSeccion(true)}>Omitir sección</button>
              )
            )}
            <button type="button" className="ci-topbar-action ci-topbar-action--exit" disabled={guardando} onClick={salirTemporalmente}>Salir por ahora</button>
          </div>
        </div>
        <div className="ci-wizard__progress" aria-hidden="true">
          <span style={{ width: `${porcentaje}%` }} />
        </div>

        <div className="ci-wizard__scroll">
          {error && pagina !== PAGINA_RESUMEN && pagina !== PAGINA_PLAN && (
            <Alert className="ci-global-alert" variant="danger">
              {mensajeSeguroDeError(error)}
            </Alert>
          )}

          {seleccionActual && elementoActual && (
            <div className="ci-question-stage">
              <div className="ci-question-meta">
                <span>{seleccionActual.bloque.Titulo}</span>
                <strong>Pregunta {indicePreguntaBloque + 1} de {elementosBloqueActual.length}</strong>
              </div>
              <button
                type="button"
                className="ci-mobile-section-action"
                disabled={guardando}
                onClick={seccionEstaOmitida ? reabrirSeccion : () => setOmitirSeccion(true)}
              >
                {seccionEstaOmitida ? "Reabrir esta sección" : "Omitir esta sección"}
              </button>
              {seleccionActual.seccion.Descripcion && (
                <p className="ci-section-description">{seleccionActual.seccion.Descripcion}</p>
              )}
              {seccionEstaOmitida ? (
                <div className="ci-omitted-state">
                  <span aria-hidden="true">✓</span>
                  <div>
                    <h1>Esta sección está omitida</h1>
                    <p>Las respuestas anteriores se conservan. Reabre la sección para consultarlas o modificarlas.</p>
                    <Button className="ci-button ci-button--primary" disabled={guardando} onClick={reabrirSeccion}>Reabrir esta sección</Button>
                  </div>
                </div>
              ) : (
                <EditorElementoConfiguracionInicial
                  key={elementoActual.Codigo}
                  elemento={elementoActual}
                  respuestas={respuestas}
                  indiceRespuestas={indiceRespuestas}
                  valoresActuales={valoresActuales}
                  sugerencias={sugerencias}
                  indiceValoresActuales={indiceValoresActuales}
                  indiceSugerencias={indiceSugerencias}
                  guardando={guardando}
                  onGuardar={guardarRespuesta}
                  onGuardarYContinuar={guardarRespuestaYAvanzar}
                  onGuardarVariasYContinuar={guardarRespuestasYAvanzar}
                  onActivar={activarElemento}
                  ubicacion={{ codigoEtapaActual: seleccionActual.bloque.Codigo }}
                  onAnterior={irAnterior}
                  onContinuar={irSiguiente}
                  etiquetaContinuar={etiquetaSiguiente}
                  anteriorDeshabilitado={anteriorDeshabilitado}
                />
              )}
            </div>
          )}

          {(pagina === PAGINA_RESUMEN || pagina === PAGINA_PLAN) && (
            <div className="ci-review-stage" ref={cierreAtencionRef} tabIndex={-1}>
              <div className="ci-review-heading">
                <p>Finalizar asistente</p>
                <h1>{intentoCierre && (preguntasPendientes.length > 0 || error) ? 'Resolvamos lo pendiente' : 'Termina a tu ritmo'}</h1>
                <span>
                  {contextoIntegracionDisponible
                    ? 'Pulsa «Finalizar y aplicar» para configurar tu empresa con tus respuestas. Puedes cambiar estos ajustes después, desde cada sección de tu cuenta.'
                    : 'Pulsa «Finalizar asistente» para guardar el plan. Esta modalidad no aplica cambios a tu empresa.'}
                </span>
              </div>
              {error && <Alert variant="danger" role="alert">{mensajeSeguroDeError(error)}</Alert>}
              {contextoIntegracionDisponible && !modoAplicacion && <Alert variant="warning">La aplicación de cambios aún no está habilitada para esta empresa. Tus respuestas están guardadas.</Alert>}
              <CierreConfiguracionInicial
                intento={intentoCierre}
                pendientes={preguntasPendientes}
                plan={plan}
                planVigente={planVigente}
                guardando={guardando}
                definicion={definicion}
                respuestas={respuestas}
                indiceRespuestas={indiceRespuestas}
                seccionesOmitidas={seccionesOmitidas}
                valoresActuales={valoresActuales}
                diferencias={diferencias}
                contextoIntegracionDisponible={contextoIntegracionDisponible}
                onIrAElemento={navegarACodigoElemento}
              />
            </div>
          )}

          {(pagina === PAGINA_RESUMEN || pagina === PAGINA_PLAN || seccionEstaOmitida) && (
            <div className="ci-wizard-footer" role="group" aria-label="Navegación del asistente">
              <button type="button" className="ci-footer-link" disabled={anteriorDeshabilitado} onClick={irAnterior}>← Anterior</button>
              <Button className="ci-button ci-button--continue" disabled={siguienteDeshabilitado} onClick={irSiguiente}>
                {etiquetaSiguiente} <span aria-hidden="true">→</span>
              </Button>
            </div>
          )}
        </div>
      </div>

      <Modal
        show={omitirSeccion}
        onHide={() => setOmitirSeccion(false)}
        centered
        className="ci-modal-layer"
        backdropClassName="ci-modal-backdrop"
        dialogClassName="ci-modal-dialog"
        contentClassName="ci-modal"
      >
        <Modal.Header closeButton><Modal.Title>Omitir sección</Modal.Title></Modal.Header>
        <Modal.Body>
          <p>Las respuestas existentes se conservarán para auditoría.</p>
          <Form.Group controlId="ci-motivo-seccion">
            <Form.Label>Motivo</Form.Label>
            <Form.Control as="textarea" rows={3} maxLength={500} value={motivoSeccion} onChange={(evento) => setMotivoSeccion(evento.target.value)} />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button className="ci-button ci-button--secondary" variant="outline-secondary" onClick={() => setOmitirSeccion(false)}>Cancelar</Button>
          <Button className="ci-button ci-button--danger" variant="danger" disabled={guardando || motivoSeccion.trim().length < 5} onClick={confirmarOmisionSeccion}>Omitir sección</Button>
        </Modal.Footer>
      </Modal>

    </div>
  );
};
